/* Standards — what each machine's efficiency is divided by: the settings
 * on its latest shift with output and the standard they give, and on the
 * ring frames and autoconers the count's SITRA norm and the standard that
 * gives. The column in use under the page's Standard switch is marked. */

import { Blueprint, PanelHead } from '../../components/ui'
import type { MachineStandard } from '../../lib/api_machines'
import { num } from '../../lib/format'
import { R, unitName } from './parts'
import type { Ctx } from './parts'

const dash = (v: number | null | undefined, d = 0) => (v === null || v === undefined ? '–' : num(v, d))

export function StandardsTab({ c }: { c: Ctx }) {
  const { data } = c
  const rows = data.standards
  const sitra = data.basis === 'sitra'
  const sources = [...new Set(rows.map((r) => r.norm_source).filter((x): x is string => !!x))]
  const inUse = (r: MachineStandard, which: 'settings' | 'sitra') =>
    (r.in_use === which ? { fontWeight: 600 } : { color: 'var(--color-neutral-600)' })

  return (
    <Blueprint className="sheet">
      <PanelHead title="What each machine is measured against"
        hint={sitra
          ? 'Standard: SITRA — ring frames and autoconers on the norm, the rest on their settings'
          : 'Standard: Settings — every machine on the sheet\u2019s own standard'} />
      <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4 }}>
        Each machine\u2019s latest shift with output in {c.monLong}. The figure in bold is the one its
        efficiency is divided by under the Standard switch above.
      </div>
      <div className="scroll-x" style={{ marginTop: 14 }}>
        <table className="table" style={{ width: '100%', minWidth: 1100 }}>
          <thead>
            <tr>
              <th>Machine</th><th>Count / hank</th><th style={R}>Positions</th>
              <th>Settings</th>
              <th style={R}>Settings gps<br /><span className="muted">g / spindle / shift</span></th>
              <th style={R}>Settings std<br /><span className="muted">kg / shift</span></th>
              <th style={R}>SITRA gps<br /><span className="muted">norm · at 100%</span></th>
              <th style={R}>SITRA std<br /><span className="muted">kg / shift</span></th>
              <th>Read on</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ fontWeight: 600 }}>{r.name}</span>
                  <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>{unitName(r.unit)} · {r.stage}</div>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.count || '–'}</td>
                <td className="num" style={{ ...R, whiteSpace: 'nowrap' }}>
                  {dash(r.positions)}
                  {r.installed && r.installed !== r.positions
                    ? <div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>of {num(r.installed)} built</div>
                    : null}
                </td>
                <td style={{ fontSize: 12, whiteSpace: 'nowrap', color: 'var(--color-neutral-700)' }}>
                  {r.spindle_rpm
                    ? `${num(r.spindle_rpm)} rpm · TPI ${dash(r.tpi, 2)}${r.tm ? ` (TM ${num(r.tm, 2)})` : ''}`
                    : r.speed_mpm ? `${num(r.speed_mpm)} m/min` : '–'}
                  {' · '}{num(r.hours)} h shift
                </td>
                <td className="num" style={{ ...R, ...inUse(r, 'settings') }}>{dash(r.settings_gps, 1)}</td>
                <td className="num" style={{ ...R, ...inUse(r, 'settings') }}>
                  {r.typed ? <span title="typed over the sheet\u2019s formula and more than three times the output — not used">typed</span> : dash(r.settings_kg)}
                </td>
                <td className="num" style={{ ...R, ...inUse(r, 'sitra') }}>
                  {r.on_norm ? (r.norm_gps ? `${num(r.norm_gps)} · ${num(r.norm_max_gps ?? 0)}` : 'no norm for this count') : '–'}
                </td>
                <td className="num" style={{ ...R, ...inUse(r, 'sitra') }}>{r.on_norm ? dash(r.sitra_kg) : '–'}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                  <span className={`tag ${r.in_use === 'sitra' ? 'tag-accent' : 'tag-outline'}`}>
                    {r.in_use === 'sitra' ? 'SITRA' : 'Settings'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pretty" style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 14, lineHeight: 1.5, maxWidth: '110ch' }}>
        <b>Settings std</b> is the sheet\u2019s own "Standard kg / Shift", worked out from the row: on a ring frame or
        simplex, spindle rpm × working spindles × shift minutes × 0.0254 ÷ (TPI × 768.1 × Ne), with TPI as typed or
        TM × √Ne; on a card, comber, draw frame or autoconer, delivery m/min × positions × shift minutes ÷ (768.1 × hank).
        An autoconer\u2019s row carries its ring frame\u2019s standard. <b>Settings gps</b> is that standard over the working
        spindles. <b>SITRA gps</b> is the count\u2019s industry norm in grams per spindle per 8-hour shift — the figure the
        Production page\u2019s target uses — with the 100%-efficiency figure beside it; <b>SITRA std</b> is the norm times the
        spindles the frame is built with, scaled to the shift length.
        {sources.length ? <> Norms from {sources.join('; ')}.</> : null} The preparatory stages have no norm on file.
      </div>
    </Blueprint>
  )
}
